import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { cn } from "@/lib/utils";

interface JsonViewerProps {
  data: any;
  title?: string;
  className?: string;
  variant?: 'default' | 'muted' | 'warning' | 'error';
}

// Format JSON with syntax highlighting
function JsonDisplay({ data }: { data: any }) {
  const jsonString = JSON.stringify(data, null, 2);

  return <pre className="overflow-x-auto whitespace-pre-wrap break-words text-xs">{jsonString}</pre>;
}

export function JsonViewer({ data, title, className, variant = 'default' }: JsonViewerProps) {
  const { t } = useTranslation('common');
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    const jsonString = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(jsonString);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const variantStyles = {
    default: 'bg-muted',
    muted: 'bg-muted/50',
    warning: 'border-yellow-200 bg-yellow-50 dark:border-yellow-900 dark:bg-yellow-900/10',
    error: 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-900/10',
  };

  return (
    <div className={cn('rounded-md border p-3', variantStyles[variant], className)}>
      {title && (
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-medium">{title}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2"
            onClick={handleCopy}
            title={t('jsonViewer.copyToClipboard')}
          >
            {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
            <span className="ml-1 text-xs">{copied ? t('jsonViewer.copied') : t('jsonViewer.copy')}</span>
          </Button>
        </div>
      )}
      <JsonDisplay data={data} />
    </div>
  );
}
